import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface UseUsernameCheckResult {
  isAvailable: boolean | null;
  isChecking: boolean;
  error: string | null;
}

export function useUsernameCheck(username: string, currentUserId?: string): UseUsernameCheckResult {
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const checkUsername = async (usernameToCheck: string) => {
      if (!usernameToCheck || usernameToCheck.length < 3) {
        setIsAvailable(null);
        setIsChecking(false);
        setError(null);
        return;
      }

      setIsChecking(true);
      setError(null);

      try {
        const { data, error } = await supabase.rpc('check_username_available', {
          _username: usernameToCheck,
          _current_user_id: currentUserId || null
        });

        if (error) throw error;

        setIsAvailable(data);
      } catch (err) {
        console.error('Username check error:', err);
        setError('Failed to check username availability');
        setIsAvailable(null);
      } finally {
        setIsChecking(false);
      }
    };

    // Debounce the username check
    if (username) {
      timeoutId = setTimeout(() => {
        checkUsername(username);
      }, 500);
    } else {
      setIsAvailable(null);
      setIsChecking(false);
      setError(null);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [username, currentUserId]);

  return { isAvailable, isChecking, error };
}